import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { useTranslate } from '@i18n';
import { Box, Button, ModalBase, ModalBody, ModalFooter, ModalHeader, u } from '@ui';

import { LiveAnalysisConfig } from './LiveAnalysisConfig';
import {
    LiveAnalysisEventParametersSelection,
    LiveAnalysisSetupParametersSelection,
} from './LiveAnalysisParametersSelection';
import { LiveAnalysisSettings } from './LiveAnalysisSettings';
import { LiveAnalysisFormValues, liveAnalysisSchema } from './schema';
import { useSaveLiveAnalysisSettings } from './useSaveLiveAnalysisSettings';

const MODAL_STEP = {
    MENU: 'MENU',
    SETUP: 'SETUP',
    EVENT: 'EVENT',
} as const;

const DEFAULT_REG_DELAY = 8;
const regDelayAtom = atomWithStorage('regDelay', DEFAULT_REG_DELAY);
const setupParametersAtom = atomWithStorage<number[]>('setupParameters', []);
const eventParametersAtom = atomWithStorage<number[]>('eventParameters', []);

export function LiveAnalysisSettingsModal({ onDismiss }: { onDismiss: () => void }) {
    const t = useTranslate();
    const [menuMode, setMenuMode] = useState<(typeof MODAL_STEP)[keyof typeof MODAL_STEP]>(MODAL_STEP.MENU);

    const settingsMutation = useSaveLiveAnalysisSettings({
        onSuccess: onDismiss,
    });

    const [regDelay, setRegDelay] = useAtom(regDelayAtom);
    const [setupParameters, setSetupParameters] = useAtom(setupParametersAtom);
    const [eventParameters, setEventParameters] = useAtom(eventParametersAtom);

    const form = useForm<LiveAnalysisFormValues>({
        mode: 'onChange',
        defaultValues: {
            setupParameters,
            eventParameters,
            regDelay: regDelay,
            autoRestart: false,
        },
        resolver: zodResolver(liveAnalysisSchema),
    });

    const selectedSetupParameters = useWatch({ control: form.control, name: 'setupParameters' });
    const selectedEventParameters = useWatch({ control: form.control, name: 'eventParameters' });

    const submitSettings = form.handleSubmit((values) => {
        setRegDelay(values.regDelay);
        setSetupParameters(values.setupParameters);
        setEventParameters(values.eventParameters);
        settingsMutation.mutateAsync(values);
    });

    return (
        <FormProvider {...form}>
            <ModalBase onDismiss={onDismiss} open variant="partial">
                {menuMode === MODAL_STEP.MENU && (
                    <>
                        <ModalHeader>Live Analysis</ModalHeader>
                        <ModalBody py={u(4)}>
                            <LiveAnalysisSettings
                                setupParameters={selectedSetupParameters}
                                eventParameters={selectedEventParameters}
                                onSetupToggle={() => setMenuMode(MODAL_STEP.SETUP)}
                                onEventToggle={() => setMenuMode(MODAL_STEP.EVENT)}
                            />
                            <LiveAnalysisConfig />
                        </ModalBody>
                        <ModalFooter
                            primary={
                                <Box display="flex" gap={u(1)}>
                                    <Button variant="secondary" size="medium" onClick={onDismiss}>
                                        {t('generic_ui.close')}
                                    </Button>{' '}
                                    <Button variant="primary" size="medium" onClick={submitSettings}>
                                        {t('generic_ui.save')}
                                    </Button>
                                </Box>
                            }
                        />
                    </>
                )}
                {menuMode === MODAL_STEP.SETUP && (
                    <LiveAnalysisSetupParametersSelection onClose={() => setMenuMode(MODAL_STEP.MENU)} />
                )}
                {menuMode === MODAL_STEP.EVENT && (
                    <LiveAnalysisEventParametersSelection onClose={() => setMenuMode(MODAL_STEP.MENU)} />
                )}
            </ModalBase>
        </FormProvider>
    );
}
